/**
 * Composition and serialization helpers over the theme token data.
 *
 * `mergeTheme` resolves a partial theme against a complete base, and
 * `tokensToCssVars` serializes a resolved theme for the CSS-custom-property
 * surface. They live apart from `tokens.ts` so that module stays pure data,
 * safe for token-only consumers like the Preact teaser.
 */

import { toHoverColor, toPressColor } from './color.helpers';
import type { PartialTheme, Theme } from './tokens';

/**
 * Merges a sparse override onto a complete base theme, shallowly per token
 * group. `avatarColors` is replaced wholesale when the override provides it;
 * `spacing` is not overridable and always comes from the base.
 * Returns `base` unchanged when there is nothing to merge.
 * @public
 */
export function mergeTheme(base: Theme, overrides: PartialTheme | null | undefined): Theme {
    if (!overrides) return base;
    return {
        colors: { ...base.colors, ...overrides.colors },
        spacing: base.spacing,
        fonts: { ...base.fonts, ...overrides.fonts },
        avatarColors: overrides.avatarColors ?? base.avatarColors,
    };
}

/**
 * Converts a resolved theme into a CSS custom property map for consumption by
 * CSS Modules via `var()`. Names match the legacy `:root` injection exactly:
 * `--{colorKey}`, `--spacing-{key}` (with a `px` suffix), and `--{fontKey}`.
 * Avatar colors are not emitted — they have no CSS consumers.
 *
 * Hover and press variants of `colorSurfacePrimary` are derived rather than
 * declared as tokens, because CSS Modules cannot call the derivation helpers
 * themselves. The design has no dedicated state colors, and predeclaring a
 * variant for every surface token would be a large surface for two consumers.
 * @public
 */
export function tokensToCssVars(theme: Theme): Record<string, string> {
    const vars: Record<string, string> = {};
    for (const [key, value] of Object.entries(theme.colors)) vars[`--${key}`] = value;
    for (const [key, value] of Object.entries(theme.spacing)) vars[`--spacing-${key}`] = `${value}px`;
    for (const [key, value] of Object.entries(theme.fonts)) vars[`--${key}`] = value;
    vars['--colorSurfacePrimaryHover'] = toHoverColor(theme.colors.colorSurfacePrimary, 0.1);
    vars['--colorSurfacePrimaryPress'] = toPressColor(theme.colors.colorSurfacePrimary, 0.2);
    vars['--colorButtonSecondaryHover'] = toHoverColor(theme.colors.colorButtonSecondary, 0.1);
    vars['--colorButtonSecondaryPress'] = toPressColor(theme.colors.colorButtonSecondary, 0.2);
    return vars;
}
